import { useCallback, useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import useEmblaCarousel from 'embla-carousel-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import useStableViewport from '@/hooks/use-stable-viewport'

import SVGIcon from '../base/svg-icon'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types/wp'

gsap.registerPlugin(ScrollTrigger)

type TimelineProps = {
  collection: {
    year: string
    pretitle: string
    title: string
    text: string
    image_1: WPImage
    image_2: WPImage
  }[]
}

const Timeline = ({ collection, ...props }: TimelineProps) => {
  const { isMobile } = useStableViewport()
  const desktopRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [slideHeight, setSlideHeight] = useState<number | undefined>(undefined)

  // Desktop carousel
  const [desktopEmblaRef, desktopEmblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start'
  })

  const [thumbsEmblaRef, thumbsEmblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    dragFree: false,
    containScroll: 'keepSnaps'
  })

  // Mobile carousel
  const [mobileEmblaRef, mobileEmblaApi] = useEmblaCarousel({
    loop: true,
    active: (collection?.length ?? 0) > 1
  })

  // Desktop: sync main + thumbs
  const onDesktopSelect = useCallback(() => {
    if (!desktopEmblaApi) return
    const index = desktopEmblaApi.selectedScrollSnap()
    setActiveIndex(index)
    thumbsEmblaApi?.scrollTo(index)
  }, [desktopEmblaApi, thumbsEmblaApi])

  const onThumbClick = useCallback(
    (index: number) => {
      if (!desktopEmblaApi) return
      desktopEmblaApi.scrollTo(index)
    },
    [desktopEmblaApi]
  )

  useEffect(() => {
    if (!desktopEmblaApi || isMobile) return

    onDesktopSelect()
    desktopEmblaApi.on('select', onDesktopSelect)
    desktopEmblaApi.on('reInit', onDesktopSelect)

    return () => {
      desktopEmblaApi.off('select', onDesktopSelect)
      desktopEmblaApi.off('reInit', onDesktopSelect)
    }
  }, [desktopEmblaApi, onDesktopSelect, isMobile])

  // Mobile: sync index + adaptive height
  useEffect(() => {
    if (!mobileEmblaApi || !isMobile) return

    const onSelect = () => {
      setActiveIndex(mobileEmblaApi.selectedScrollSnap())

      const slides = mobileEmblaApi.slideNodes()
      const index = mobileEmblaApi.selectedScrollSnap()
      const activeSlide = slides[index]
      if (activeSlide) {
        setSlideHeight(activeSlide.scrollHeight)
      }
    }

    onSelect()
    mobileEmblaApi.on('select', onSelect)
    mobileEmblaApi.on('reInit', onSelect)

    return () => {
      mobileEmblaApi.off('select', onSelect)
      mobileEmblaApi.off('reInit', onSelect)
    }
  }, [mobileEmblaApi, isMobile])

  // Reset height on desktop
  useEffect(() => {
    if (!isMobile) {
      setSlideHeight(undefined)
    }
  }, [isMobile])

  // Desktop: GSAP reveal
  useGSAP(
    () => {
      if (!desktopRef.current || isMobile) return

      const selector = gsap.utils.selector(desktopRef)
      const carousel = selector('[data-motion-carousel]')[0]

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: desktopRef.current,
          start: 'top 80%'
        }
      })

      timeline.fromTo(
        carousel,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 1.2, ease: 'power2.out' }
      )

      return () => {
        timeline.kill()
      }
    },
    {
      scope: desktopRef,
      dependencies: [isMobile]
    }
  )

  // Mobile: GSAP fadeIn
  const mobileMotionRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!isMobile || !mobileMotionRef.current) return

      gsap.fromTo(
        mobileMotionRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mobileMotionRef.current,
            once: true,
            start: 'top 82%',
            invalidateOnRefresh: true,
            toggleActions: 'play none none none'
          }
        }
      )
    },
    {
      scope: mobileMotionRef,
      dependencies: [collection, isMobile],
      revertOnUpdate: true
    }
  )

  const getThumbStyle = useCallback(
    (index: number) => {
      const prevIndex = activeIndex - 1
      const nextIndex = activeIndex + 1
      const prev2Index = activeIndex - 2
      const next2Index = activeIndex + 2

      if (index === activeIndex) {
        return { height: '26px', opacity: 1 }
      }

      if (index === prevIndex || index === nextIndex) {
        return { height: '14px', opacity: 0.5 }
      }

      if (index === prev2Index || index === next2Index) {
        return { height: '10px', opacity: 0.5 }
      }

      return { height: '5px', opacity: 0.5 }
    },
    [activeIndex]
  )

  if (!collection?.length) return null

  const renderDesktop = () => (
    <Section {...props} className="overflow-hidden">
      <Container ref={desktopRef}>
        <Spacer variant="xxl" />

        <div
          ref={desktopEmblaRef}
          className="overflow-hidden"
          data-motion-carousel
        >
          <div className="flex">
            {collection.map((item, index) => (
              <div
                key={`timeline-item-${index}`}
                className="min-w-0 flex-[0_0_100%]"
              >
                <Grid>
                  <div
                    className={cn(
                      'relative h-auto md:col-span-1 md:h-[55vw] lg:col-span-2'
                    )}
                  >
                    <div
                      className={cn(
                        'text-trim left-0 top-0 md:absolute',
                        'pb-spacing-md md:origin-[100%_0%] md:-translate-x-full md:translate-y-[100%] md:-rotate-90 md:pb-0 md:text-[120px] lg:text-[16vw]'
                      )}
                    >
                      {item.year}
                    </div>
                  </div>

                  <div
                    className={cn(
                      'md:col-span-2 lg:col-span-4',
                      'md:border-l-[1px] md:border-l-[rgba(255,255,255,.25)]'
                    )}
                  >
                    <div className="flex flex-col pb-spacing-2xl">
                      <div className="relative aspect-square w-[73%] rounded-radius-minimal object-cover">
                        <img
                          src={item.image_1.url}
                          alt={item.image_1.alt}
                          className="absolute left-0 top-0 aspect-square size-full object-cover"
                        />
                      </div>

                      <div className="relative aspect-square w-[27%] place-self-end rounded-radius-minimal object-cover">
                        <img
                          src={item.image_2.url}
                          alt={item.image_2.alt}
                          className="absolute left-0 top-0 aspect-square size-full object-cover"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      'flex flex-col gap-spacing-md md:col-span-3 lg:col-span-4 lg:col-start-9',
                      'mb-[120px] md:pt-[74%]'
                    )}
                  >
                    <Typography
                      variant="regular"
                      className="text-text-primary-light dark:text-text-primary-dark"
                    >
                      {item.pretitle}
                    </Typography>

                    <Typography variant="h4">{item.title}</Typography>

                    <Typography
                      variant="regular"
                      className="text-text-primary-light dark:text-text-primary-dark"
                    >
                      {item.text}
                    </Typography>
                  </div>
                </Grid>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex translate-y-[-100%] items-end justify-between">
          <div className="flex flex-[1_0_auto] justify-center gap-spacing-2xs">
            <button
              className="flex size-[48px] items-center justify-center rounded-full border-[1px] border-[rgba(255,255,255,.25)] bg-[#0d0d0d] transition-colors duration-300 hover:border-[rgba(255,255,255,.15)]"
              aria-label="Previous"
              onClick={() => desktopEmblaApi?.scrollPrev()}
            >
              <SVGIcon
                symbol="arrow-prev"
                className="size-[16px] text-[white]"
              />
            </button>
            <button
              className="flex size-[48px] items-center justify-center rounded-full border-[1px] border-[rgba(255,255,255,.25)] bg-[#0d0d0d] transition-colors duration-300 hover:border-[rgba(255,255,255,.15)]"
              aria-label="Next"
              onClick={() => desktopEmblaApi?.scrollNext()}
            >
              <SVGIcon
                symbol="arrow-next"
                className="size-[16px] text-[white]"
              />
            </button>
          </div>
          <div ref={thumbsEmblaRef}>
            <div className="flex items-end justify-end gap-0">
              {collection.map((item, index) => {
                const style = getThumbStyle(index)

                return (
                  <button
                    key={`timeline-thumb-${index}`}
                    type="button"
                    onClick={() => onThumbClick(index)}
                    className="relative flex h-[80px] flex-[0_0_16px] items-end justify-center"
                    aria-label={`${item.year}`}
                    aria-current={index === activeIndex}
                  >
                    <div
                      className="w-[1px] rounded-full bg-[currentColor] transition-all duration-500"
                      style={{
                        height: style.height,
                        opacity: style.opacity
                      }}
                    />
                    <div
                      className={cn(
                        'text-small pointer-events-none absolute left-1/2 top-0 mb-spacing-md -translate-x-1/2 text-center opacity-0 transition-opacity duration-300',
                        {
                          'opacity-100': index === activeIndex
                        }
                      )}
                    >
                      {collection[activeIndex]?.year}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <Spacer variant="xxl" />
      </Container>
    </Section>
  )

  const renderMobile = () => (
    <Section {...props} className="overflow-hidden px-padding-sm py-spacing-xl">
      <div ref={mobileMotionRef} data-motion-gallery className="opacity-0">
        <div
          ref={mobileEmblaRef}
          className="ease-out overflow-hidden transition-[height] duration-500"
          style={slideHeight ? { height: slideHeight } : undefined}
        >
        <div className="flex items-start">
          {collection.map((item, index) => (
            <div
              key={`timeline-mobile-${index}`}
              className="min-w-0 flex-[0_0_100%]"
            >
              <div className="pb-spacing-md text-s-h1">{item.year}</div>

              <div className="flex flex-col pb-spacing-md">
                <div className="relative aspect-square w-[73%] rounded-radius-minimal object-cover">
                  <img
                    src={item.image_1.url}
                    alt={item.image_1.alt}
                    className="absolute left-0 top-0 aspect-square size-full object-cover"
                  />
                </div>

                <div className="relative aspect-square w-[27%] place-self-end rounded-radius-minimal object-cover">
                  <img
                    src={item.image_2.url}
                    alt={item.image_2.alt}
                    className="absolute left-0 top-0 aspect-square size-full object-cover"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-spacing-md pb-spacing-2xl">
                <Typography
                  variant="regular"
                  className="text-text-primary-light dark:text-text-primary-dark"
                >
                  {item.pretitle}
                </Typography>

                <Typography variant="h4">{item.title}</Typography>

                <Typography
                  variant="regular"
                  className="text-text-primary-light dark:text-text-primary-dark"
                >
                  {item.text}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>
      {collection.length > 1 && (
        <div className="flex items-center justify-between pt-spacing-lg">
          <Typography variant="large" className="condensed">
            {(activeIndex + 1).toString().padStart(2, '0')}/
            {collection.length.toString().padStart(2, '0')}
          </Typography>
          <div className="flex items-end justify-end gap-spacing-2xs self-end">
            <button
              className="flex size-[32px] items-center justify-center rounded-full border-[1px] border-[rgba(255,255,255,.25)] bg-[#0d0d0d] transition-colors duration-300 hover:border-[rgba(255,255,255,.15)]"
              aria-label="Previous"
              onClick={() => mobileEmblaApi?.scrollPrev()}
            >
              <SVGIcon
                symbol="arrow-prev"
                className="size-[12px] text-[white]"
              />
            </button>
            <button
              className="flex size-[32px] items-center justify-center rounded-full border-[1px] border-[rgba(255,255,255,.25)] bg-[#0d0d0d] transition-colors duration-300 hover:border-[rgba(255,255,255,.15)]"
              aria-label="Next"
              onClick={() => mobileEmblaApi?.scrollNext()}
            >
              <SVGIcon
                symbol="arrow-next"
                className="size-[12px] text-[white]"
              />
            </button>
          </div>
        </div>
      )}
      </div>
    </Section>
  )

  return <>{isMobile ? renderMobile() : renderDesktop()}</>
}

export default Timeline
